<template>
  <v-container
    role="region"
    aria-labelledby="ShopPacks"
  >
    <v-flex
      :class="{'column': $vuetify.breakpoint.mdAndDown, 'row': $vuetify.breakpoint.lgAndUp}"
      wrap
    >
      <v-app-bar
        flat
        color="primary"
        role="navigation"
        aria-label="Shop Packs Navigation"
      >
        <v-toolbar-title
          id="ShopPacks"
          class="text-body-2"
        >
          {{ $t(`components.shop.${$props.category}.title`) }}
        </v-toolbar-title>
      </v-app-bar>
      <v-flex
        v-show="isLoading"
        xs12
        sm12
        md12
        lg12
        role="status"
        aria-live="polite"
      >
        <v-progress-linear
          indeterminate
          color="accent"
          aria-label="Loading shop packs"
        />
      </v-flex>

      <!-- Error alerts -->
      <v-flex
        flex
        xs12
        sm12
        md12
        lg12
        role="region"
        aria-label="Shop Error Alerts"
      >
        <div
          v-for="(errorsGroup, errorsGroupIndex) in filteredErrors"
          :key="errorsGroupIndex"
        >
          <v-alert
            v-for="error in errorsGroup"
            :key="error.id"
            :type="error.type"
            :icon="error.icon"
            role="alert"
            aria-live="assertive"
          >
            {{ error.msg }}
          </v-alert>
        </div>
        <v-alert
          v-model="isCopied"
          dismissible
          type="success"
          role="status"
          aria-live="polite"
        >
          {{ $t('components.shop.copied') }}
        </v-alert>
      </v-flex>
      <v-layout
        v-show="!hasPacks && !isLoading"
        :class="{'column': $vuetify.breakpoint.mdAndDown, 'row': $vuetify.breakpoint.lgAndUp}"
        wrap
      >
        <v-flex
          xs12
          sm12
          md12
          lg12
          text-center
        >
          <v-alert type="info">
            {{ $t('components.shop.noContent') }}
          </v-alert>
        </v-flex>
      </v-layout>
      <v-layout
        v-show="hasPacks"
        :class="{'column': $vuetify.breakpoint.mdAndDown, 'row': $vuetify.breakpoint.lgAndUp}"
        wrap
      >
        <v-flex
          v-show="!isLoading && packs.length > perPage"
          xs12
          sm12
          md12
          align-center
          justify-center
          text-center
          mt-1
          mb-1
        >
          <v-pagination
            v-model="page"
            :value="page"
            :length="(packs.length - 1) || 0"
            :total-visible="perPage"
            color="accent"
            next-icon="fad fa-chevron-right"
            prev-icon="fad fa-chevron-left"
            aria-label="Pagination"
            @input="onPaginate"
          />
        </v-flex>
        <v-flex
          xs12
          sm12
          md12
          column
          :class="{ 'lg3 ml-11-6': $vuetify.breakpoint.lgAndUp, 'lg12': $vuetify.breakpoint.mdAndDown}"
        >
          <v-card>
            <v-card-title class="pb-0">
              <h4>{{ $t('components.shop.cart.title') }}</h4>
            </v-card-title>
            <v-alert
              :value="zpRemainingAfterBuy < 0"
              type="error"
            >
              {{ $t('components.shop.cart.notEnoughZp', {
                points: Math.abs(zpRemainingAfterBuy)
              }) }}
            </v-alert>
            <v-list dense>
              <v-list-item v-if="isCompletedOAuth">
                <v-list-item-content>{{ $t('components.shop.cart.points') }}</v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ zushinPoints }}
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-show="zpRemainingAfterBuy >= 0 && zpRemainingAfterBuy != zushinPoints || zpRemainingAfterBuy < 0 && zpRemainingAfterBuy != zushinPoints"
              >
                <v-list-item-content
                  v-show="zpRemainingAfterBuy >= 0 && zpRemainingAfterBuy != zushinPoints"
                >
                  {{ $t('components.shop.cart.zpAfter') }}
                </v-list-item-content>
                <v-list-item-content
                  v-show="zpRemainingAfterBuy >= 0 && zpRemainingAfterBuy != zushinPoints"
                  class="align-end"
                >
                  {{ zpRemainingAfterBuy }}
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="cart.length" />
              <v-list
                v-if="cart.length"
                dense
                two-line
              >
                <v-list-item
                  v-for="item in toCart"
                  :key="toCart.indexOf(item)"
                >
                  <v-list-item-content>{{ item.amount }}x {{ item.title }} - {{ item.price }} {{ $t('components.shop.cart.zpshort') }}</v-list-item-content>
                  <v-list-item-action>
                    <v-flex
                      xs12
                      sm12
                      md12
                      lg12
                      align-center
                      justify-space-between
                      row
                      wrap
                    >
                      <v-btn
                        :aria-label="'Increase {{item.title}}'"
                        icon
                        small
                        color="accent"
                        @click="increasePack(item.packId)"
                        @keydown.enter="increasePack(item.packId)"
                        @keydown.space.prevent="increasePack(item.packId)"
                      >
                        <v-icon
                          small
                          color="white"
                        >
                          fad fa-plus
                        </v-icon>
                      </v-btn>
                      <v-btn
                        :aria-label="'Decrease {{item.title}}'"
                        icon
                        small
                        color="accent"
                        @click="decreasePack(item.packId)"
                        @keydown.enter="decreasePack(item.packId)"
                        @keydown.space.prevent="decreasePack(item.packId)"
                      >
                        <v-icon
                          small
                          color="white"
                        >
                          fad fa-minus
                        </v-icon>
                      </v-btn>
                    </v-flex>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-divider v-show="cart.length" />
              <v-list-item v-show="cart.length">
                <v-list-item-content>{{ $t('components.shop.cart.total') }}</v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ $t('components.shop.cart.price', {
                    total: cartTotal
                  }) }}
                </v-list-item-content>
              </v-list-item>
              <v-flex
                v-show="isCompletedOAuth && cart.length && zpRemainingAfterBuy >= 0 && zpRemainingAfterBuy != zushinPoints && !isBuying"
                xs12
                sm12
                md12
                lg12
              >
                <v-btn
                  :aria-label="$t('components.shop.buttons.buy')"
                  block
                  small
                  color="accent"
                  class="font-weight-thin"
                  @click="BuyCart()"
                >
                  {{ $t('components.shop.buttons.buy') }}
                  <v-icon
                    small
                    righta-box-heart
                  />
                </v-btn>
              </v-flex>
              <v-flex
                v-show="isBuying"
                xs12
                sm12
                md12
                lg12
              >
                <v-progress-linear
                  color="accent"
                  indeterminate
                />
              </v-flex>
            </v-list>
          </v-card>
        </v-flex>
        <v-flex
          v-for="pack in packs[page]"
          :key="pack.id"
          xs12
          sm12
          md12
          :class="{ 'lg7 ml-1': $vuetify.breakpoint.lgAndUp && packs[page].length === 1, 'lg4 ml-2': packs[page].length > 1}"
        >
          <v-card
            v-if="pack.isActive"
            :class="{ 'shop-pack' : true, 'shop-pack-selected': parseInt($route.params.packId, 0) === pack.id}"
            fill-height
            :ripple="pack.hasTimeLimit && !pack.isExpired"
            role="button"
            tabindex="0"
            @click="AddToCart(pack)"
            @keyup.enter="AddToCart(pack)"
            @keyup.space.prevent="AddToCart(pack)"
          >
            <!-- Image -->
            <v-img
              :alt="pack.title"
              :src="pack.img"
              height="100px"
            >
              <v-chip
                v-if="pack.currentPrice"
                color="accent"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
              >
                <v-icon left>
                  fad fa-money-bill-alt
                </v-icon>
                {{ $t(`components.shop.pack.price`, {
                  price: pack.currentPrice
                }) }}
              </v-chip>
              <v-chip
                v-if="pack.hasDiscount"
                color="primary"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                :value="pack.hasDiscount"
              >
                <v-icon left>
                  fad fa-tag
                </v-icon>
                {{ $t(`components.shop.pack.origPrice`, {
                  original: pack.price
                }) }}
              </v-chip>
            </v-img>

            <v-card-title primary-title>
              <div>
                <div class="h5">
                  {{ pack.title }}
                </div>
              </div>
            </v-card-title>
            <v-card-text :class="{'shop-pack-badges': $vuetify.breakpoint.lgAndUp}">
              <v-chip
                v-if="pack.hasLevels && !pack.isSameLevel"
                color="accent"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                :value="pack.hasLevels && pack.isSameLevel"
              >
                <v-avatar class="accent darken-4 py-7 px-7 mx-2 my-2">
                  {{ pack.maxLevel }}
                </v-avatar>
                {{ $t(`components.shop.pack.level`) }}
              </v-chip>
              <v-chip
                v-if="pack.hasLevels && !pack.isSameLevel"
                color="accent"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                :value="pack.hasLevels && !pack.isSameLevel"
              >
                <v-avatar class="accent darken-4 py-7 px-7 mx-2 my-2">
                  {{ pack.minLevel }}
                </v-avatar>
                {{ $t(`components.shop.pack.minLevel`) }}
              </v-chip>
              <v-chip
                v-if="pack.hasLevels && !pack.isSameLevel"
                color="accent"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                :value="pack.hasLevels && !pack.isSameLevel"
              >
                <v-avatar class="accent darken-4 py-7 px-7 mx-2 my-2">
                  {{ pack.maxLevel }}
                </v-avatar>
                {{ $t(`components.shop.pack.maxLevel`) }}
              </v-chip>
              <v-chip
                v-if="pack.hasMaxPerUser"
                color="accent"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                :value="pack.hasMaxPerUser"
              >
                <v-avatar class="accent darken-4 py-7 px-7 mx-2 my-2">
                  {{ pack.maxPerUser }}
                </v-avatar>
                {{ $t(`components.shop.pack.maxPerUser`) }}
              </v-chip>
              <v-chip
                v-if="pack.hasMax"
                color="accent"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                :value="pack.hasMax"
              >
                <v-avatar class="accent darken-4 py-7 px-7 mx-2 my-2">
                  {{ pack.totalBought }}/{{ pack.max }}
                </v-avatar>
                {{ $t(`components.shop.pack.maximumSale`) }}
              </v-chip>
              <v-chip
                v-if="pack.hasDiscount"
                color="red"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                :value="pack.hasDiscount"
              >
                <v-icon left>
                  fad fa-fire
                </v-icon>
                {{ $t(`components.shop.pack.hasDiscount`, {
                  percentage: toPercentage(pack.currentPrice, pack.price)
                }) }}
              </v-chip>
              <v-chip
                color="accent"
                text-color="white"
                class="py-1 px-1 mx-1 my-1"
                role="button"
                tabindex="0"
                @click.stop="toLink(pack.id)"
                @keyup.enter.stop="toLink(pack.id)"
                @keyup.space.prevent.stop="toLink(pack.id)"
              >
                <v-icon left>
                  fad fa-share
                </v-icon>
                {{ $t(`components.shop.pack.share`) }}
              </v-chip>
              <count-down
                v-show="pack.hasTimeLimit"
                :text="$t(`components.shop.pack.timeLimit`)"
                :ms="pack.timeLeft"
                @expired="onExpired(pack.id)"
              />
            </v-card-text>

            <!-- Item list -->
            <v-card-text>
              <v-subheader
                card
                color="primary"
              >
                {{ $t(`components.shop.pack.itemsTitle`) }}
              </v-subheader>
              <v-list dense>
                <v-list-item
                  v-for="item in pack.items"
                  :key="pack.items.indexOf(item)"
                >
                  <v-list-item-content>
                    {{ item.amount }}x {{ item.name }}
                    <b
                      :v-show="item.isBound"
                    >{{ $t(`components.shop.pack.bound`) }}</b>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-alert
              v-if="pack.hasTimeLimit > 0"
              :value="pack.isExpired"
              type="warning"
            >
              {{ $t('components.shop.warnings.expired') }}
            </v-alert>
            <v-flex
              xs12
              sm12
              md12
              lg12
            >
              <v-btn
                v-if="(!pack.hasTimeLimit || pack.hasTimeLimit && !pack.isExpired) && !isBuying"
                aria-label="Add to cart"
                text
                icon
                color="accent"
              >
                <v-icon small>
                  fad fa-cart-plus
                </v-icon>
              </v-btn>
              <v-spacer />
            </v-flex>
          </v-card>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-container>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import { mapState, mapGetters } from "vuex";
import { filterByKeys } from "@/utils/object.utils";
import { toShopPacks, toCart, getTotalPrice } from "@/utils/shop.utils";
import { toPercentage } from "@/utils/math.utils";
import { copyToClipboard } from "@/utils/text.utils"
import config from "@/constants/config";
import Validator from "@/modules/validator.module";
import CountDown from "@/components/Views/CountDownBadge";
import actions from "@/store/actions";
import links from "@/constants/links";

const ValidatorModule = new Validator();

export default {
  name: "ShopPacksList",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    ValidationProvider,
    CountDown
  },
  inject: ["$validator"],
  props: {
    category: {
        type: String,
        default: ""
    },
    charId: {
        type: Number,
        default: 0
    },
  },
  data() {
    return {
        isLoading: true,
        isBuying: false,
        page: 1,
        perPage: config.shopPacksPerPage,
        packs: [],
        cart: [],
        isCopied: false
    }
  },
  computed: {
    ...mapGetters(['zushinPoints']),
    ...mapState({
      zpRemainingAfterBuy(state) {
        return this.zushinPoints - getTotalPrice(this.cart);
      }
    }),
    // Check if is logged
    isCompletedOAuth() {
      return this.$store.getters.isLogged && this.$store.getters.username;
    },
    toCart() {
      return toCart(this.cart);
    },
    cartTotal() {
      return getTotalPrice(this.cart);
    },
    filteredErrors() {
      return filterByKeys(this.$validator.errors.collect(), []);
    },
    hasPacks() {
      return !this.isLoading
        ? this.packs.length
          ? this.packs[this.page] && this.packs[this.page].length > 0
          : false
        : false;
    }
  },
  watch: {
    $route(to, from) {
      if (to.name != from.name) {
        // this.$ga.event("shop", "category-view", to.name);
        this.page = 1;
      }
    },
    category() {
      this.getPacks();
    }
  },
  beforeMount() {
    this.getPacks();
    this.page = parseInt(this.$route.params.page, 0) || 1;
  },
  methods: {
    /**
     * Share product
     */
    toLink(packId) {
      const packLink = `${window.location.protocol}//${
        window.location.hostname
      }${links.routes.shop
        .replace(":page", this.page)
        .replace(":packId?", packId)}/${this.category}`;
      // Copy text
      copyToClipboard(packLink).then(() => {
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 5000);
      });
    },
    /**
     * Validation to check if can buy the cart
     */
    canBuy() {
      if (!this.charId) {
        ValidatorModule.add("buy-packs", {
          type: 0,
          msg: this.$t("components.shop.cart.notSelectedChar")
        });
        return false;
      } else {
        return true;
      }
    },
    // Buy an shop package
    BuyCart() {
      if (this.canBuy()) {
        this.isBuying = true;
        this.$api
          .BuyShopPack(this.toCart, this.$props.charId)
          .then(msg => {
            ValidatorModule.add("buy-packs", msg);
            this.cart = [];
            actions.updateZP(msg.points);
            this.isBuying = false;
            // this.$ga.event("shop", "buy", this.$props.charId);
          })
          .catch((err) => {
            if(err.status >= 500){
                ValidatorModule.add("buy-packs", {
                type: 0,
                msg: this.$t("components.common.errors.apiError")
                });
            }

            this.isBuying = false;
          });
      }
    },
    // Add to cart shop package
    AddToCart(pack) {
        const canBuy = (!pack.hasTimeLimit || pack.hasTimeLimit && !pack.isExpired) && !this.isBuying;

        if (canBuy) {
            this.cart.push(pack);

            // this.$ga.event("shop", "add-pack", pack.title);
        }
    },
    // Get the packs according the current $props.category
    getPacks() {
      this.isLoading = true;

      this.$api.getShopPacks(this.$props.category).then(packs => {
        this.packs = toShopPacks(packs, this.$route.params.packId);
        this.isLoading = false;
      });
    },
    /**
    /* Add the current pack as expired pack
    /* @param number packId - Pack id that will be declared as expired
    **/
    onExpired(packId) {
        if (typeof this.packs[this.page] !== 'object') {
            return;
        }

        const pack = this.packs[this.page].find((pack) => pack.id == packId) || {};
        const index = this.packs[this.page].indexOf(pack);

        if (!pack.id || !index) {
            return;
        }

        this.packs[this.page][index].isExpired = true;
    },
    /**
    /* Handles on $next, $previous, $number for pagintion
    /* @param number num - The current new page
    **/
    onPaginate(num) {
      this.page = num;

      this.$router.replace({
        params: {
          page: num,
          packId: this.$route.params.packId || undefined
        }
      });
    //   this.$ga.event("shop", `page-${this.category}`, num);
    },
    /**
     * Get percentage of discount
     * @param number currentPrice - Current discount price
     * @param number origPrice - current original price
     */
    toPercentage(currentPrice, origPrice) {
      return toPercentage(currentPrice, origPrice);
    },
    /**
     * Increase pack total
     * @param number packId - The pack that will be duplicated
     **/
    increasePack(packId) {
      const pack = this.cart.find(({ id }) => id === packId) || {};

      if (pack) {
        this.cart.push(pack);
      }

    //   this.$ga.event("shop", "pack-increase", pack.title);
    },
    /**
     * Decrease pack total
     * @param number packId - The pack that will be reduced from the amount or removed
     **/
    decreasePack(packId) {
      const pack = this.cart.find(({ id }) => id === packId) || {};

      if (Object.keys(pack).length > 0) {
        this.cart.splice(this.cart.lastIndexOf(pack), 1);

        // this.$ga.event("shop", "pack-decrease", pack.title);
      }
    }
  }
};
</script>
